import React, { useState, useEffect } from 'react';
import { Button, Form, Alert, ProgressBar, Card } from 'react-bootstrap';
import { useUser } from '../../context/UserContext';
import { useTenant } from '../../context/TenantContext';
import { useMembership } from '../../context/MembershipContext';
import {
  updateDocument,
  uploadAndCreateDocument,
  uploadDocumentFiles,
} from '../../services/documentsService';
import { fetchUnitsForTenant } from '../../services/unitsService';
import { normalizeAttachments } from '../../platform/contentAttachments';
import ContentAttachmentsEditor from '../../platform/ContentAttachmentsEditor';
import './DocumentCreate.css';
import { useToast } from '../../platform/toast/ToastProvider';

function DocumentCreate({ onDocumentCreated, documentToEdit }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [unit, setUnit] = useState('general');
  const [unitName, setUnitName] = useState('Splošno');
  const [units, setUnits] = useState([]);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const { user } = useUser();
  const { tenantId } = useTenant();
  const { memberships } = useMembership();
  const toast = useToast();
  const isEditMode = Boolean(documentToEdit);

  useEffect(() => {
    if (documentToEdit) {
      setTitle(documentToEdit.title || '');
      setDescription(documentToEdit.description || '');
      setUnit(documentToEdit.unit || 'general');
      setUnitName(documentToEdit.unitName || 'Splošno');
      setExistingAttachments(normalizeAttachments(documentToEdit));
      setNewFiles([]);
    }
  }, [documentToEdit]);

  useEffect(() => {
    if (!user || !tenantId) return;

    const loadUnits = async () => {
      const unitsList = await fetchUnitsForTenant(tenantId, { user, memberships });
      setUnits(unitsList);
    };

    loadUnits();
  }, [user, tenantId, memberships]);

  const handleUnitChange = (e) => {
    const selectedUnitId = e.target.value;
    const selectedUnit = units.find((u) => u.id === selectedUnitId);
    setUnit(selectedUnitId);
    setUnitName(selectedUnit ? selectedUnit.unitName : 'Splošno');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      setError('Za nalaganje dokumenta morate biti prijavljeni.');
      return;
    }
    if (!title.trim() || !description.trim()) {
      setError('Naslov in opis sta obvezna.');
      return;
    }

    const totalAttachments = existingAttachments.length + newFiles.length;
    if (totalAttachments === 0) {
      setError('Vsaj ena datoteka je obvezna.');
      return;
    }

    try {
      setUploading(true);
      setError('');

      if (isEditMode) {
        const uploadedAttachments = newFiles.length
          ? await uploadDocumentFiles(newFiles, setUploadProgress, tenantId)
          : [];

        await updateDocument(documentToEdit.id, {
          title: title.trim(),
          description: description.trim(),
          unit,
          unitName,
          attachments: [...existingAttachments, ...uploadedAttachments],
        });
      } else {
        await uploadAndCreateDocument(user, {
          activeTenantId: tenantId,
          title: title.trim(),
          description: description.trim(),
          files: newFiles,
          unit,
          unitName,
          onProgress: setUploadProgress,
        });
      }

      toast.success(isEditMode ? 'Dokument je bil posodobljen.' : 'Dokument je bil naložen.');
      onDocumentCreated();
    } catch (uploadError) {
      console.error('Error saving document:', uploadError);
      const message = isEditMode ? 'Posodobitev dokumenta ni uspela.' : 'Nalaganje dokumenta ni uspelo.';
      setError(message);
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="document-create">
      {error && <Alert variant="danger">{error}</Alert>}

      <Form onSubmit={handleSubmit}>
        <Card className="document-create-card platform-content-detail-card">
          <Card.Header className="document-create-card__header">
            <h2 className="document-create-card__title">
              {isEditMode ? 'Uredi dokument' : 'Novi dokument'}
            </h2>
            <p className="document-create-card__subtitle">
              {isEditMode
                ? 'Posodobite podatke dokumenta in upravljajte priložene datoteke.'
                : 'Naložite pravilnik, obrazec ali druge datoteke za člane.'}
            </p>
          </Card.Header>
          <Card.Body className="document-create-card__body">
            <Form.Group className="document-create-field">
              <Form.Label>Naslov</Form.Label>
              <Form.Control
                type="text"
                placeholder="Vnesite naslov dokumenta"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                className="document-create-title-input"
              />
            </Form.Group>

            <Form.Group className="document-create-field">
              <Form.Label>Opis</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Kratek opis vsebine dokumenta"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </Form.Group>

            <Form.Group className="document-create-field">
              <Form.Label>Enota</Form.Label>
              <Form.Select value={unit} onChange={handleUnitChange}>
                <option value="general">Splošno</option>
                {units.map((unitObj) => (
                  <option key={unitObj.id} value={unitObj.id}>
                    {unitObj.unitName}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <ContentAttachmentsEditor
              label="Datoteke"
              required
              existingAttachments={existingAttachments}
              onExistingAttachmentsChange={setExistingAttachments}
              newFiles={newFiles}
              onNewFilesChange={setNewFiles}
              hint="Izberite eno ali več datotek. Ob urejanju lahko dodate nove ali odstranite obstoječe."
            />

            {uploading && (
              <ProgressBar
                now={uploadProgress}
                label={`${Math.round(uploadProgress)}%`}
                striped
                className="document-create-progress"
              />
            )}

            <Button variant="primary" type="submit" disabled={uploading}>
              {uploading
                ? (isEditMode ? 'Shranjujem…' : 'Nalagam…')
                : (isEditMode ? 'Shrani spremembe' : 'Naloži dokument')}
            </Button>
          </Card.Body>
        </Card>
      </Form>
    </div>
  );
}

export default DocumentCreate;
